import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { MapPin } from "lucide-react";
import { toast } from "sonner";

/**
 * GoogleMapMeasurement - Property measurement tool with on-demand edge labels
 * 
 * Refactored UI (Jan 2026):
 * - Removed always-on per-edge InfoWindow labels to reduce visual clutter
 * - Implemented on-demand segment highlighting: hover (desktop) / tap (mobile)
 * - Preserved summary totals box and vertex markers
 * - No changes to measurement calculation or data flow
 */
export const GoogleMapMeasurement = ({
  onAddressSelected,
  selectedAddress,
  mapCenter,
  mapZoom,
  onMapStateChange,
  onMeasurementUpdate,
  onClearMeasurements,
}) => {
  const mapRef = useRef(null);
  const addressInputRef = useRef(null);
  const [map, setMap] = useState(null);
  const [drawingMgr, setDrawingMgr] = useState(null);
  const [overlays, setOverlays] = useState([]);
  const [lastResult, setLastResult] = useState("");

  const totalsRef = useRef({ feet: 0, areaSqFt: 0, perimeterFt: 0 });
  const allMarkersRef = useRef([]);
  
  // On-demand tooltip state (single tooltip, shown on hover/tap)
  const activeTooltipRef = useRef(null);
  const segmentPolylinesRef = useRef([]); // Invisible polylines for hover detection

  // ===========================================================
  // 🔄 CLEAR EVERYTHING
  // ===========================================================
  const clearAll = () => {
    overlays.forEach((o) => o.setMap(null));
    allMarkersRef.current.forEach((m) => m.setMap(null));
    segmentPolylinesRef.current.forEach((p) => p.setMap(null));
    if (activeTooltipRef.current) {
      activeTooltipRef.current.close();
      activeTooltipRef.current = null;
    }
    allMarkersRef.current = [];
    segmentPolylinesRef.current = [];
    setOverlays([]);
    totalsRef.current = { feet: 0, areaSqFt: 0, perimeterFt: 0 };
    setLastResult("No measurements yet");

    const label = document.getElementById("measureLabel");
    if (label) label.innerText = "No measurements yet";

    // P0 sync fix (Tier 2): propagate zeroed measurement to parent React
    // state so Calculator inputs / staleness signals / Quote Summary can
    // react. Without this, clearAll only mutated internal refs + DOM.
    if (onMeasurementUpdate) {
      onMeasurementUpdate({ lineFeet: 0, areaSqFt: 0, perimeterFt: 0 });
    }
    if (typeof onClearMeasurements === 'function') {
      onClearMeasurements();
    }

    toast.success("All measurements cleared");
  };

  useEffect(() => {
    if (!window.google || !window.google.maps) {
      setTimeout(initMap, 300);
    } else initMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initMap = () => {
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: mapCenter || { lat: 41.3, lng: -72.9 },
      zoom: mapZoom || 17,
      mapTypeId: "satellite",
      mapTypeControl: true,
      streetViewControl: false,
    });
    setMap(mapInstance);

    if (mapCenter) {
      mapInstance.setCenter(mapCenter);
    }
    if (mapZoom) {
      mapInstance.setZoom(mapZoom);
    }

    // ---- address autocomplete ----
    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      componentRestrictions: { country: "us" },
      fields: ["geometry", "formatted_address", "address_components"],
    });
    const marker = new window.google.maps.Marker({ map: mapInstance });

    if (selectedAddress?.formatted) {
      addressInputRef.current.value = selectedAddress.formatted;
    }

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;

      clearAll();
      const loc = place.geometry.location;
      mapInstance.setCenter(loc);
      mapInstance.setZoom(19);
      marker.setPosition(loc);

      // Extract address components
      const components = place.address_components || [];
      const getComponent = (type) => 
        components.find((c) => c.types.includes(type))?.long_name || '';
      const getComponentShort = (type) => 
        components.find((c) => c.types.includes(type))?.short_name || '';

      const streetNumber = getComponent("street_number");
      const streetName = getComponent("route");
      const city = getComponent("locality") || getComponent("sublocality") || getComponent("administrative_area_level_2");
      const state = getComponentShort("administrative_area_level_1");
      const zip = getComponent("postal_code");
      const street = [streetNumber, streetName].filter(Boolean).join(" ");

      if (state && onAddressSelected) {
        onAddressSelected({
          formatted: place.formatted_address,
          formatted_address: place.formatted_address,
          street,
          city,
          state,
          zip,
          postalCode: zip,
          lat: loc.lat(),
          lng: loc.lng(),
        });
      }

      if (onMapStateChange) {
        const center = loc.toJSON();
        onMapStateChange(center, 19);
      }
    });

    // ---- drawing manager ----
    const mgr = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: window.google.maps.ControlPosition.TOP_CENTER,
        drawingModes: ["polyline", "polygon"],
      },
      polygonOptions: {
        fillColor: "#f26b1d",
        fillOpacity: 0.35,
        strokeColor: "#f26b1d",
        strokeWeight: 2,
        clickable: true,
        editable: true,
      },
      polylineOptions: {
        strokeColor: "#f26b1d",
        strokeWeight: 2,
        clickable: true,
        editable: true,
      },
    });
    mgr.setMap(mapInstance);
    setDrawingMgr(mgr);

    // Notify parent of map state whenever it settles
    if (onMapStateChange) {
      mapInstance.addListener("idle", () => {
        const center = mapInstance.getCenter()?.toJSON();
        const zoom = mapInstance.getZoom();
        if (center && zoom != null) {
          onMapStateChange(center, zoom);
        }
      });
    }

    // ---- summary label box ----
    const ensureLabel = () => {
      let label = document.getElementById("measureLabel");
      if (!label) {
        label = document.createElement("div");
        label.id = "measureLabel";
        Object.assign(label.style, {
          position: "absolute",
          bottom: "10px",
          right: "10px",
          background: "#f26b1d",
          color: "#fff",
          padding: "8px 12px",
          borderRadius: "8px",
          fontWeight: "600",
          fontSize: "13px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          maxWidth: "280px",
          lineHeight: "1.4",
        });
        mapRef.current.appendChild(label);
      }
      return label;
    };

    const updateLabel = (label) => {
      const { feet, areaSqFt, perimeterFt } = totalsRef.current;
      let parts = [];
      if (feet > 0) parts.push(`📏 ${feet.toFixed(1)} ft`);
      if (areaSqFt > 0) parts.push(`📐 ${Math.round(areaSqFt)} sq ft`);
      if (perimeterFt > 0) parts.push(`⭕ ${perimeterFt.toFixed(1)} ft perimeter`);
      label.innerText = parts.join("\n") || "No measurements yet";
      setLastResult(parts.join(" | ") || "No measurements yet");
    };

    // ---- vertex markers (small circles at each point) ----
    const createVertexMarker = (latLng) => {
      const m = new window.google.maps.Marker({
        position: latLng,
        map: mapInstance,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: "#ffffff",
          fillOpacity: 1,
          strokeColor: "#f26b1d",
          strokeWeight: 2,
          scale: 5,
        },
        clickable: false,
      });
      allMarkersRef.current.push(m);
      return m;
    };

    // ---- on-demand segment tooltip (hover/tap) ----
    const showSegmentTooltip = (midpoint, text) => {
      // Close any existing tooltip
      if (activeTooltipRef.current) {
        activeTooltipRef.current.close();
      }
      
      const tooltip = new window.google.maps.InfoWindow({
        content: `<div style="font-size:12px;font-weight:600;color:#222;padding:2px 4px;white-space:nowrap;">${text}</div>`,
        position: midpoint,
        disableAutoPan: true,
      });
      tooltip.open(mapInstance);
      activeTooltipRef.current = tooltip;
    };

    const hideSegmentTooltip = () => {
      if (activeTooltipRef.current) {
        activeTooltipRef.current.close();
        activeTooltipRef.current = null;
      }
    };

    // ---- invisible segment polylines for hover detection ----
    const createSegmentHitArea = (p1, p2, distFt, isPolygon) => {
      const midpoint = new window.google.maps.LatLng(
        (p1.lat() + p2.lat()) / 2,
        (p1.lng() + p2.lng()) / 2
      );

      // Create a wider invisible polyline for easier hover detection
      const hitArea = new window.google.maps.Polyline({
        path: [p1, p2],
        strokeColor: "#f26b1d",
        strokeOpacity: 0, // Invisible by default
        strokeWeight: 12, // Wide hit area
        map: mapInstance,
        clickable: true,
        zIndex: 100,
      });

      // Store segment data for highlighting
      hitArea._segmentData = {
        p1,
        p2,
        midpoint,
        distFt,
        isPolygon,
      };

      // Desktop: hover events
      hitArea.addListener("mouseover", () => {
        hitArea.setOptions({ strokeOpacity: 0.6, strokeWeight: 4 });
        showSegmentTooltip(midpoint, `${distFt.toFixed(1)} ft`);
      });

      hitArea.addListener("mouseout", () => {
        hitArea.setOptions({ strokeOpacity: 0, strokeWeight: 12 });
        hideSegmentTooltip();
      });

      // Mobile: tap events
      hitArea.addListener("click", () => {
        // Toggle: if this segment's tooltip is showing, hide it; otherwise show it
        const currentTooltip = activeTooltipRef.current;
        if (currentTooltip && currentTooltip._segmentId === hitArea) {
          hideSegmentTooltip();
          hitArea.setOptions({ strokeOpacity: 0, strokeWeight: 12 });
        } else {
          // Reset all segments
          segmentPolylinesRef.current.forEach((seg) => {
            seg.setOptions({ strokeOpacity: 0, strokeWeight: 12 });
          });
          // Highlight this segment
          hitArea.setOptions({ strokeOpacity: 0.6, strokeWeight: 4 });
          showSegmentTooltip(midpoint, `${distFt.toFixed(1)} ft`);
          if (activeTooltipRef.current) {
            activeTooltipRef.current._segmentId = hitArea;
          }
        }
      });

      segmentPolylinesRef.current.push(hitArea);
      return hitArea;
    };

    // Clear segment hit areas for a shape
    const clearSegmentHitAreas = () => {
      segmentPolylinesRef.current.forEach((p) => p.setMap(null));
      segmentPolylinesRef.current = [];
      hideSegmentTooltip();
    };

    // ===========================================================
    // 📏 POLYLINE COMPLETE
    // ===========================================================
    window.google.maps.event.addListener(mgr, "polylinecomplete", (polyline) => {
      polyline.setEditable(true);
      const path = polyline.getPath();
      setOverlays((prev) => [...prev, polyline]);
      const label = ensureLabel();
      const shapeMarkers = [];

      const updateTotals = () => {
        // Clear previous markers and hit areas
        shapeMarkers.forEach((m) => m.setMap(null));
        shapeMarkers.length = 0;
        clearSegmentHitAreas();

        // Create vertex markers
        path.forEach((p) => {
          const m = createVertexMarker(p);
          shapeMarkers.push(m);
        });

        // Create segment hit areas (for on-demand labels)
        for (let i = 0; i < path.getLength() - 1; i++) {
          const p1 = path.getAt(i);
          const p2 = path.getAt(i + 1);
          const distM = window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
          const distFt = distM * 3.28084;
          createSegmentHitArea(p1, p2, distFt, false);
        }

        // Calculate totals (unchanged logic)
        const lenM = window.google.maps.geometry.spherical.computeLength(path);
        const lenFt = lenM * 3.28084;
        const prevLen = polyline._lastLengthFt || 0;
        totalsRef.current.feet += lenFt - prevLen;
        polyline._lastLengthFt = lenFt;
        
        if (onMeasurementUpdate) {
          onMeasurementUpdate({
            lineFeet: totalsRef.current.feet,
            areaSqFt: totalsRef.current.areaSqFt,
            perimeterFt: totalsRef.current.perimeterFt,
          });
        }

        updateLabel(label);
      };

      updateTotals();
      window.google.maps.event.addListener(path, "insert_at", updateTotals);
      window.google.maps.event.addListener(path, "set_at", updateTotals);
    });

    // ===========================================================
    // ⬟ POLYGON COMPLETE
    // ===========================================================
    window.google.maps.event.addListener(mgr, "polygoncomplete", (polygon) => {
      polygon.setEditable(true);
      const path = polygon.getPath();
      setOverlays((prev) => [...prev, polygon]);
      const label = ensureLabel();
      const shapeMarkers = [];

      const updateTotals = () => {
        // Clear previous markers and hit areas
        shapeMarkers.forEach((m) => m.setMap(null));
        shapeMarkers.length = 0;
        clearSegmentHitAreas();

        // Create vertex markers
        path.forEach((p) => {
          const m = createVertexMarker(p);
          shapeMarkers.push(m);
        });

        // Create segment hit areas (for on-demand labels)
        for (let i = 0; i < path.getLength(); i++) {
          const p1 = path.getAt(i);
          const p2 = path.getAt((i + 1) % path.getLength());
          const distM = window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
          const distFt = distM * 3.28084;
          createSegmentHitArea(p1, p2, distFt, true);
        }

        // Calculate totals (unchanged logic)
        const lenM = window.google.maps.geometry.spherical.computeLength(path);
        const lenFt = lenM * 3.28084;
        const areaM = window.google.maps.geometry.spherical.computeArea(path);
        const sqft = areaM * 10.7639;

        const prevArea = polygon._lastAreaSqFt || 0;
        const prevPerim = polygon._lastPerimeterFt || 0;
        totalsRef.current.areaSqFt += sqft - prevArea;
        totalsRef.current.perimeterFt += lenFt - prevPerim;
        polygon._lastAreaSqFt = sqft;
        polygon._lastPerimeterFt = lenFt;
        
        if (onMeasurementUpdate) {
          onMeasurementUpdate({
            lineFeet: totalsRef.current.feet,
            areaSqFt: totalsRef.current.areaSqFt,
            perimeterFt: totalsRef.current.perimeterFt,
          });
        }

        updateLabel(label);
      };

      updateTotals();
      window.google.maps.event.addListener(path, "insert_at", updateTotals);
      window.google.maps.event.addListener(path, "set_at", updateTotals);
    });

    // Close tooltip when clicking on map (not on segment)
    mapInstance.addListener("click", () => {
      hideSegmentTooltip();
      segmentPolylinesRef.current.forEach((seg) => {
        seg.setOptions({ strokeOpacity: 0, strokeWeight: 12 });
      });
    });
  };

  return (
    <div className="space-y-4">
      {/* Address */}
      <div className="space-y-2">
        <Label htmlFor="address" className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" /> Property Address
        </Label>
        <Input
          ref={addressInputRef}
          id="address"
          placeholder="Search address..."
          className="border-input focus:border-primary focus:ring-primary/20"
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => drawingMgr?.setDrawingMode("polyline")} variant="outline">
          📏 Measure Line
        </Button>
        <Button onClick={() => drawingMgr?.setDrawingMode("polygon")} variant="outline">
          ⬟ Measure Area
        </Button>
        <Button onClick={clearAll} variant="destructive">
          Clear
        </Button>
      </div>

      {/* Map */}
      <div
        ref={mapRef}
        className="w-full h-[420px] rounded-xl border-2 border-border shadow-md overflow-hidden"
        style={{ position: "relative" }}
      />

      {/* How-to Guide */}
      <div className="bg-accent/50 rounded-lg p-3 text-xs text-muted-foreground leading-relaxed">
        <p className="font-medium text-accent-foreground mb-1">📏 How to Measure:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Search for the property address above.</li>
          <li>Click <b>📏 Measure Line</b> or <b>⬟ Measure Area</b>.</li>
          <li>
            For a <b>line</b>, click to place points. <b>Double-click</b> to finish.
          </li>
          <li>
            For a <b>polygon</b>, click around the perimeter; it closes automatically.
          </li>
          <li><b>Hover over any edge</b> to see its individual measurement.</li>
        </ol>
      </div>

      {lastResult && (
        <div className="text-xs text-muted-foreground text-center">{lastResult}</div>
      )}
    </div>
  );
};

export default GoogleMapMeasurement;
